/**
 * Auto-lock Service
 * Automatically locks the app after period of inactivity
 */

export type AutoLockListener = (isLocked: boolean) => void;

class AutoLockService {
  private isLocked = false;
  private lockTimeout: NodeJS.Timeout | null = null;
  private inactivityTimeout = 5 * 60 * 1000; // 5 minutes default
  private listeners: AutoLockListener[] = [];
  private lastActivityTime = Date.now();

  constructor() {
    if (typeof window !== 'undefined') {
      this.setupEventListeners();
    }
  }

  private setupEventListeners() {
    // Track user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    activityEvents.forEach(event => {
      document.addEventListener(event, () => this.handleActivity(), { passive: true });
    });

    // Handle visibility change (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.handleInactivity();
      } else {
        this.handleActivity();
      }
    });

    // Handle page unload
    window.addEventListener('beforeunload', () => {
      this.saveState();
    });

    // Restore state on load
    this.restoreState();
  }

  private handleActivity() {
    this.lastActivityTime = Date.now();
    
    // Don't reset timer if already locked
    if (this.isLocked) {
      return;
    }

    // Reset inactivity timer
    this.resetLockTimer();
  }

  private handleInactivity() {
    // Start countdown to lock
    this.resetLockTimer();
  }

  private resetLockTimer() {
    // Clear existing timer
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
    }

    // Don't set timer if already locked or timeout is 0 (disabled)
    if (this.isLocked || this.inactivityTimeout === 0) {
      return;
    }

    // Set new timer
    this.lockTimeout = setTimeout(() => {
      this.lock();
    }, this.inactivityTimeout);
  }

  /**
   * Lock the app
   */
  lock() {
    if (this.isLocked) return;

    this.isLocked = true;
    this.saveState();
    this.notifyListeners(true);

    // Clear timer
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
      this.lockTimeout = null;
    }
  }

  /**
   * Unlock the app
   */
  unlock() {
    if (!this.isLocked) return;

    this.isLocked = false;
    this.lastActivityTime = Date.now();
    this.saveState();
    this.notifyListeners(false);

    // Restart inactivity timer
    this.resetLockTimer();
  }

  /**
   * Check if app is locked
   */
  getIsLocked(): boolean {
    return this.isLocked;
  }

  /**
   * Set inactivity timeout
   */
  setTimeout(minutes: number) {
    if (minutes < 0) {
      throw new Error('Timeout must be positive');
    }

    this.inactivityTimeout = minutes * 60 * 1000;
    
    // Save to storage
    localStorage.setItem('autolock_timeout', String(minutes));

    // Reset timer with new timeout
    if (!this.isLocked) {
      this.resetLockTimer();
    }
  }

  /**
   * Get current timeout in minutes
   */
  getTimeout(): number {
    return this.inactivityTimeout / (60 * 1000);
  }

  /**
   * Enable auto-lock
   */
  enable() {
    if (this.inactivityTimeout === 0) {
      this.setTimeout(5); // Default 5 minutes
    } else {
      this.resetLockTimer();
    }
  }

  /**
   * Disable auto-lock
   */
  disable() {
    if (this.lockTimeout) {
      clearTimeout(this.lockTimeout);
      this.lockTimeout = null;
    }
    this.inactivityTimeout = 0;
    localStorage.setItem('autolock_timeout', '0');
  }

  /**
   * Check if auto-lock is enabled
   */
  isEnabled(): boolean {
    return this.inactivityTimeout > 0;
  }

  /**
   * Get time until auto-lock (in seconds)
   */
  getTimeUntilLock(): number {
    if (this.isLocked || this.inactivityTimeout === 0) {
      return 0;
    }

    const elapsed = Date.now() - this.lastActivityTime;
    const remaining = this.inactivityTimeout - elapsed;
    
    return Math.max(0, Math.ceil(remaining / 1000));
  }

  /**
   * Add listener for lock state changes
   */
  addListener(listener: AutoLockListener) {
    this.listeners.push(listener);
  }

  /**
   * Remove listener
   */
  removeListener(listener: AutoLockListener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private notifyListeners(isLocked: boolean) {
    this.listeners.forEach(listener => listener(isLocked));
  }

  private saveState() {
    localStorage.setItem('autolock_state', JSON.stringify({
      isLocked: this.isLocked,
      lastActivityTime: this.lastActivityTime,
      inactivityTimeout: this.inactivityTimeout,
    }));
  }

  private restoreState() {
    const stored = localStorage.getItem('autolock_state');
    if (!stored) {
      // Load timeout setting
      const timeoutSetting = localStorage.getItem('autolock_timeout');
      if (timeoutSetting) {
        this.inactivityTimeout = parseInt(timeoutSetting) * 60 * 1000;
      }
      return;
    }

    try {
      const state = JSON.parse(stored);
      
      // Restore timeout
      this.inactivityTimeout = state.inactivityTimeout || this.inactivityTimeout;
      
      // Check if should be locked based on elapsed time
      const elapsed = Date.now() - state.lastActivityTime;
      if (state.isLocked || (this.inactivityTimeout > 0 && elapsed > this.inactivityTimeout)) {
        this.isLocked = true;
        this.notifyListeners(true);
      } else {
        // Resume with remaining time
        this.lastActivityTime = state.lastActivityTime;
        this.resetLockTimer();
      }
    } catch (error) {
      console.error('Failed to restore auto-lock state:', error);
    }
  }
}

export const autoLockService = new AutoLockService();
